// auth.js (updated)

const AUTH_KEY = "irecruit_auth";
const REFRESH_URL = "/api/auth/refresh"; // <-- change to your backend route
const CLOCK_SKEW_S = 30; // tolerate small clock drift

/**
 * Persist auth. Backward-compatible, but also accepts refreshToken & tokenType.
 * @param {{ token:string, user:Object, refreshToken?:string, tokenType?:string }} data
 */
export function setAuth(data) {
	if (data?.token && data?.user) {
		const toSave = {
			token: data.token,
			user: data.user,
		};
		if (data.refreshToken) toSave.refreshToken = data.refreshToken;
		if (data.tokenType) toSave.tokenType = data.tokenType; // e.g., "Bearer"
		localStorage.setItem(AUTH_KEY, JSON.stringify(toSave));
	}
}

/** Internal helper: read saved auth JSON safely */
export function getAuthData() {
	try {
		return JSON.parse(localStorage.getItem(AUTH_KEY));
	} catch {
		return null;
	}
}

/** Returns the raw access token string (no refresh logic) */
export function getToken() {
	return getAuthData()?.token || null;
}

/** "Bearer" by default unless you’ve stored something else */
export function getTokenType() {
	return getAuthData()?.tokenType || "Bearer";
}

/** Full user object */
export function getUser() {
	return getAuthData()?.user || null;
}

/** User’s role helper */
export function getUserRole() {
	return getUser()?.role || null;
}

/** Decode JWT payload safely (returns null for non-JWT tokens) */
function parseJwt(token) {
	try {
		const parts = token.split(".");
		if (parts.length !== 3) return null;
		const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
		const json = decodeURIComponent(
			atob(b64)
				.split("")
				.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
				.join("")
		);
		return JSON.parse(json);
	} catch {
		return null;
	}
}

function isJwtExpired(token, skewSeconds = CLOCK_SKEW_S) {
	const payload = parseJwt(token);
	if (!payload?.exp) return false; // treat non-JWT or no exp as not-expired (cannot verify)
	const now = Math.floor(Date.now() / 1000);
	return payload.exp <= now + skewSeconds;
}

/**
 * Try to refresh the access token using a stored refreshToken.
 * Expects the backend to respond with { token?, accessToken?, refreshToken?, user?, tokenType? }.
 * Returns the new access token string on success, or null on failure.
 */
async function refreshAccessToken(refreshUrl = REFRESH_URL) {
	const data = getAuthData();
	const refreshToken = data?.refreshToken || data?.refresh_token;
	if (!refreshToken) return null;

	try {
		const res = await fetch(refreshUrl, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ refreshToken }),
			// include credentials/cookies if your backend uses them:
			// credentials: "include",
		});
		if (!res.ok) return null;
		const json = await res.json();

		const newToken = json.token || json.accessToken || json.access_token || null;
		if (!newToken) return null;

		const newRefresh = json.refreshToken || json.refresh_token || refreshToken;
		const tokenType = json.tokenType || json.token_type || data?.tokenType || "Bearer";
		const user = json.user || data.user || null;

		setAuth({ token: newToken, user, refreshToken: newRefresh, tokenType });
		return newToken;
	} catch {
		return null;
	}
}

/**
 * ✅ getAccessToken
 * Returns a valid access token string, optionally refreshing if it’s (nearly) expired.
 * If refresh fails and `autoLogout` is true, it clears auth and redirects.
 *
 * @param {{ refresh?: boolean, autoLogout?: boolean }} [opts]
 * @returns {Promise<string|null>}
 */
export async function getAccessToken(opts = {}) {
	const { refresh = true, autoLogout = true } = opts;
	const token = getToken();
	if (!token) return null;

	// If it's a JWT and expired/near-expired, try to refresh:
	if (refresh && isJwtExpired(token)) {
		const newTok = await refreshAccessToken();
		if (newTok) return newTok;
		if (autoLogout) logout();
		return null;
	}

	// Non-JWT or still valid JWT:
	return token;
}

/**
 * Convenience for fetch: returns { Authorization: "<type> <token>" } or {}
 * @param {{ refresh?: boolean, autoLogout?: boolean }} [opts]
 * @returns {Promise<Record<string,string>>}
 */
export async function getAuthHeader(opts = {}) {
	const token = await getAccessToken(opts);
	if (!token) return {};
	return { Authorization: `${getTokenType()} ${token}` };
}

/** Synchronous auth check (no refresh) */
export function isAuthenticated() {
	const token = getToken();
	if (!token) return false;
	try {
		// If JWT, verify exp; if not a JWT, treat presence as "authenticated"
		const payload = parseJwt(token);
		if (!payload?.exp) return true;
		const now = Math.floor(Date.now() / 1000);
		return payload.exp > now;
	} catch {
		return false;
	}
}

/** Clear auth and redirect (adjust path if your login page differs) */
export function logout() {
	localStorage.removeItem(AUTH_KEY);
	window.location.replace("/app/pages/login.html");
}
